import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import CruiseDestinationsSectionItem from './CruiseDestinationsSectionItem'
import TrackingContext from 'contexts/trackingContext'
import qs from 'qs'
import Heading from 'LuxKit/Typography/Heading'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

type DestinationMessageKey =
  | 'australiaNewZealandSouthPacific'
  | 'asia'
  | 'northernEurope'
  | 'mediterranean'
  | 'alaska'
  | 'japan'
  | 'northAmerica'
  | 'latinAmerica'
  | 'caribbean'
  | 'ukGermanyFrance'

const destinationMessages = defineMessages({
  australiaNewZealandSouthPacific: {
    id: 'w6erk9XCvawpwTWMLjJd',
    defaultMessage: 'Australia, New Zealand & South Pacific',
    description: 'Cruise destination tile label for Australia, New Zealand and South Pacific on the cruises landing page',
  },
  asia: {
    id: 'd6EKp36FoXLnvJjur8OS',
    defaultMessage: 'Asia',
    description: 'Cruise destination tile label for Asia on the cruises landing page',
  },
  northernEurope: {
    id: 'FsuJCA6giPD1DJJj1RcC',
    defaultMessage: 'Northern Europe',
    description: 'Cruise destination tile label for Northern Europe on the cruises landing page',
  },
  mediterranean: {
    id: '__BrFcfSBJEmgOQq1PYn',
    defaultMessage: 'Mediterranean',
    description: 'Cruise destination tile label for Mediterranean on the cruises landing page',
  },
  alaska: {
    id: 'rUwiCOFtg-DYmGKOHNVS',
    defaultMessage: 'Alaska',
    description: 'Cruise destination tile label for Alaska on the cruises landing page',
  },
  japan: {
    id: 'KDPkKiz-LJin-ZcWGHGv',
    defaultMessage: 'Japan',
    description: 'Cruise destination tile label for Japan on the cruises landing page',
  },
  northAmerica: {
    id: 'SqL7Gfy-v4TcuPUKj8E8',
    defaultMessage: 'North America',
    description: 'Cruise destination tile label for North America on the cruises landing page',
  },
  latinAmerica: {
    id: '2mk5F5HHQqCdnAihCeXj',
    defaultMessage: 'Latin America',
    description: 'Cruise destination tile label for Latin America on the cruises landing page',
  },
  caribbean: {
    id: 'Bq2F_NC-wq8icK422z5m',
    defaultMessage: 'Caribbean',
    description: 'Cruise destination tile label for the Caribbean on the cruises landing page',
  },
  ukGermanyFrance: {
    id: 'YKXgWLHKexuJSFNOo4aY',
    defaultMessage: 'UK, Germany & France',
    description: 'Cruise destination tile label for the United Kingdom, Germany and France on the cruises landing page',
  },
})

type DestinationData = {
  destination: string
  displayNameKey: DestinationMessageKey
  image: { id: string }
  filter: string
}

const DESTINATION_DATA: Array<DestinationData> = [
  {
    destination: 'Australia, New Zealand & South Pacific',
    displayNameKey: 'australiaNewZealandSouthPacific',
    image: { id: 'wnrf8ety2m2q2wl5wk' },
    filter: qs.stringify({
      destinationIds: 'le_d3d9446802a44259755d38e6d163e820,le_9fc3d7152ba9336a670e36d0ed79bc43,le_a8697c7e6e7988b29a9de094b8d554cc',
      destinationNames: 'Australia, New Zealand & South Pacific',
    }),
  },
  {
    destination: 'Asia',
    displayNameKey: 'asia',
    image: { id: '4mwhzxy5zfk7lbocwyk' },
    filter: qs.stringify({
      destinationIds: 'le_fdbf7de11c1a054dc927adf66d9b5fce',
      destinationNames: 'Asia',
    }),
  },
  {
    destination: 'Northern Europe',
    displayNameKey: 'northernEurope',
    image: { id: 'edext8oubg3vyfjdj8d' },
    filter: qs.stringify({
      destinationIds: 'le_93d9ce9e859971d13fa6475ee1f6cb57',
      destinationNames: 'Northern Europe',
    }),
  },
  {
    destination: 'Mediterranean',
    displayNameKey: 'mediterranean',
    image: { id: 'deh51ookkm0egm5dy8w6' },
    filter: qs.stringify({
      destinationIds: 'le_93db85ed909c13838ff95ccfa94cebd9,le_d6eddba497017a4a5055ea593b073444,le_fa6fb501bac39d314fdc5a3d5a33c2e3,le_8af415d7ba15f25a61db66d54137d8d7',
      destinationNames: 'Mediterranean',
    }),
  },
  {
    destination: 'Alaska',
    displayNameKey: 'alaska',
    image: { id: '9mb0t3bhrrkleb9ad2ik' },
    filter: qs.stringify({
      destinationIds: 'le_e2c0be24560d78c5e599c2a9c9d0bbd2',
      destinationNames: 'Alaska',
    }),
  },
  {
    destination: 'Japan',
    displayNameKey: 'japan',
    image: { id: 'rombr02mrd6t1i9pxi3' },
    filter: qs.stringify({
      destinationIds: 'le_7647966b7343c29048673252e490f736',
      destinationNames: 'Japan',
    }),
  },
]

const INTERNATIONAL_DESTINATION_DATA: Array<DestinationData> = [
  {
    destination: 'Mediterranean',
    displayNameKey: 'mediterranean',
    image: { id: 'deh51ookkm0egm5dy8w6' },
    filter: qs.stringify({
      destinationIds: 'le_93db85ed909c13838ff95ccfa94cebd9,le_d6eddba497017a4a5055ea593b073444,le_fa6fb501bac39d314fdc5a3d5a33c2e3,le_8af415d7ba15f25a61db66d54137d8d7',
      destinationNames: 'Mediterranean',
    }),
  },
  {
    destination: 'North America',
    displayNameKey: 'northAmerica',
    image: { id: 'p28suam4m7fnt7csh7' },
    filter: qs.stringify({
      destinationIds: 'le_cf874aad79e14b401a4c86954a596fa5',
      destinationNames: 'North America',
    }),
  },
  {
    destination: 'Northern Europe',
    displayNameKey: 'northernEurope',
    image: { id: 'edext8oubg3vyfjdj8d' },
    filter: qs.stringify({
      destinationIds: 'le_93d9ce9e859971d13fa6475ee1f6cb57',
      destinationNames: 'Northern Europe',
    }),
  },
  {
    destination: 'Latin America',
    displayNameKey: 'latinAmerica',
    image: { id: '1zjvd5zptmkmbj13lb55' },
    filter: qs.stringify({
      destinationIds: 'le_ded8e3f6fa8da10375ce4f89e1124893',
      destinationNames: 'Latin America',
    }),
  },
  {
    destination: 'Caribbean',
    displayNameKey: 'caribbean',
    image: { id: 'bihvmclzh6v5r5shea7' },
    filter: qs.stringify({
      destinationIds: 'le_8c34c92797a64e0dfe9c9cf718a277ad,le_ca9975a613d90f72ad5aff1b9aae502d,le_d7bce4d8dbac13945471c343c4973ba3',
      destinationNames: 'Caribbean',
    }),
  },
  {
    destination: 'UK, Germany & France',
    displayNameKey: 'ukGermanyFrance',
    image: { id: 'nnijsnnfnb21a1kaez1' },
    filter: qs.stringify({
      destinationIds: 'le_cfecdb276f634854f3ef915e2e980c31,le_ea5d2f1c4608232e07d3aa3d998e5135,le_093f65e080a295f8076b1c5722a46aa2',
      destinationNames: 'UK, Germany & France',
    }),
  },
]

function CruisePageDestinationTiles() {
  const intl = useIntl()
  const isInternationalMarket = useCruiseInternationalMarket()
  const destinationData = isInternationalMarket ? INTERNATIONAL_DESTINATION_DATA : DESTINATION_DATA

  const locations = destinationData.map((location) => ({
    ...location,
    name: intl.formatMessage(
      {
        id: 'MIDBHycAUBOWzyBt3C69',
        defaultMessage: '{destination} Cruises',
        description: 'Tile heading for cruises to a specific destination',
      },
      { destination: intl.formatMessage(destinationMessages[location.displayNameKey]) },
    ),
  }))

  const viewOffersLabel = intl.formatMessage({
    id: 'pBWBnTLrBfCsDhpRiekY',
    defaultMessage: 'View offers',
    description: 'Call to action button text to view cruise offers for a destination',
  })

  return (
    <TrackingContext.Provider
      value={{
        category: 'cruise_destinations',
        label: 'Cruise Page Destinations',
        listId: 'cruise-destinations-carousel',
        listName: 'Cruise Page Destinations',
      }}
    >
      <Group gap={32} direction="vertical">
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="BZPJSyfsuwraI_lmSxRj"
              defaultMessage="Popular cruise <i>destinations</i>"
              description="Section heading for popular cruise destinations in the cruises section"
              values={{ i: (chunks) => <i>{chunks}</i> }}
            />
          </Heading>
        </LayoutContainer>

        <LayoutContainer>
          <TrackedCardCarousel pageSize={6} width={rem(180)}>
            {locations.map((location, index) => (
              <CruiseDestinationsSectionItem
                key={location.destination}
                destination={location}
                position={index}
                href={`/search/cruises?${location.filter}`}
                image={location.image}
                heading={location.name}
                subHeading=""
                align="top"
                callToAction={viewOffersLabel}
              />
            ))}
          </TrackedCardCarousel>
        </LayoutContainer>
      </Group>
    </TrackingContext.Provider>
  )
}

export default CruisePageDestinationTiles
